import React, { useState } from 'react';
import { apiRequest } from '../../utils/apiHelper';

import { useDataTable } from '../hooks/useDataTable';
import DataTable from '../components/shared/DataTable';
import PageHeader from '../components/shared/PageHeader';
import StatusBadge from '../components/shared/StatusBadge';
import ConfirmModal from '../components/shared/ConfirmModal';
import CarrierModal from './CarrierModal';
import ActionDrawer from '../components/shared/ActionDrawer';
import { useModal } from '../hooks/useModal';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import { showServerError, showSuccess, showError } from '../../utils/toastHelper';

function CarrierManagement() {
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const {
    isOpen: isCarrierOpen,
    open: openCarrier,
    close: closeCarrier
  } = useModal();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [carrierToDelete, setCarrierToDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const { user } = useAuth();
  const isShippingStaff = user?.role === 'SHIPPING_STAFF';
  const baseEndpoint = isShippingStaff ? '/shipping/carriers' : '/admin/carriers';

  const {
    data: carriers,
    loading,
    pagination,
    handlePageChange,
    handleLimitChange,
    refresh
  } = useDataTable(baseEndpoint, {
    initialFilters: {}
  });

  const handleAddCarrier = () => {
    setSelectedCarrier(null);
    openCarrier();
  };

  const handleEditCarrier = (carrier) => {
    setSelectedCarrier(carrier);
    openCarrier();
  };

  const handleModalSuccess = () => {
    refresh();
    closeCarrier();
    setSelectedCarrier(null);
  };

  const handleStatusChange = async (carrier) => {
    try {
      setActionLoading(true);
      const newStatus = carrier.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      const response = await apiRequest(`${baseEndpoint}/${carrier.id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });

      if (response.success) {
        showSuccess('Kargo firması durumu güncellendi');
        refresh();
      } else {
        throw new Error(response.message || 'Durum güncellenirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Failed to update carrier status:', error);
      if (error.apiError) {
        showServerError();
      } else {
        showError(error.message || 'Durum güncellenirken bir hata oluştu');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCarrier = (carrier) => {
    setCarrierToDelete(carrier);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!carrierToDelete) return;

    try {
      setActionLoading(true);
      const response = await apiRequest(`${baseEndpoint}/${carrierToDelete.id}`, {
        method: 'DELETE'
      });

      if (response.success) {
        showSuccess('Kargo firması başarıyla silindi');
        setShowDeleteConfirm(false);
        setCarrierToDelete(null);
        refresh();
      } else {
        throw new Error(response.message || 'Kargo firması silinirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Failed to delete carrier:', error);
      if (error.apiError) {
        showServerError();
      } else {
        showError(error.message || 'Kargo firması silinirken bir hata oluştu');
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Kargo personeli yalnızca okuma yetkisine sahip — CRUD aksiyonları gizlenir
  const baseColumns = [
    {
      header: 'Firma Adı',
      accessor: 'name',
      render: (row) => (
        <span className="text-truncate d-inline-block" style={{ maxWidth: '150px' }}>
          {row.name}
        </span>
      )
    },
    {
      header: 'Kod',
      accessor: 'code',
      render: (row) => row.code
    },
    {
      header: 'Durum',
      accessor: 'status',
      render: (row) => <StatusBadge status={row.status} />
    },
    {
      header: 'Oluşturulma Tarihi',
      accessor: 'createdAt',
      render: (row) => new Date(row.createdAt).toLocaleDateString('tr-TR')
    }
  ];

  const actionsColumn = {
    header: 'İşlemler',
    accessor: 'actions',
    render: (row) => (
      <div className="dropdown" onClick={(e) => e.stopPropagation()}>
        <button className="btn btn-sm btn-light text-muted" data-bs-toggle="dropdown">
          <i className="fas fa-ellipsis-v"></i>
        </button>
        <ul className="dropdown-menu dropdown-menu-end border-0 shadow">
          <li>
            <button className="dropdown-item" onClick={() => handleEditCarrier(row)}>
              <i className="fas fa-edit me-2"></i> Düzenle
            </button>
          </li>
          <li>
            <button
              className="dropdown-item"
              onClick={() => handleStatusChange(row)}
              disabled={actionLoading}
            >
              <i className={`fas fa-${row.status === 'ACTIVE' ? 'ban' : 'check'} me-2`}></i>
              {row.status === 'ACTIVE' ? 'Pasif Yap' : 'Aktif Yap'}
            </button>
          </li>
          <li>
            <button className="dropdown-item text-danger" onClick={() => handleDeleteCarrier(row)}>
              <i className="fas fa-trash me-2"></i> Sil
            </button>
          </li>
        </ul>
      </div>
    )
  };

  const columns = isShippingStaff ? baseColumns : [...baseColumns, actionsColumn];

  // Show full page loader on initial load
  if (loading && (!carriers || carriers.length === 0)) {
    return (
      <div className="container-fluid p-2">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <LoadingSpinner message="Kargo firmaları yükleniyor..." size="medium" />
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-2">
      <PageHeader
        title="Kargo Firmaları"
        description="Tüm kargo firmalarını buradan yönetebilirsiniz"
        breadcrumbs={[{ label: 'Dashboard', path: '/admin' }, { label: 'Kargo Firmaları' }]}
        actions={!isShippingStaff && (
          <button className="btn btn-primary btn-sm-mobile" onClick={handleAddCarrier}>
            <i className="fas fa-plus me-2"></i> Yeni Kargo Firması
          </button>
        )}
      />

      {/* Content */}
      <DataTable
        tableId="carriers"
        columns={columns}
        data={carriers}
        loading={loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onRowClick={handleEditCarrier}
        emptyState={{
          title: 'Kargo Firması Bulunamadı',
          message: 'Gösterilecek kargo firması bulunmuyor.',
          icon: 'truck'
        }}
      />

      {/* Modals */}
      {/* Carrier Drawer */}
      <CarrierModal
        isOpen={isCarrierOpen}
        onClose={() => {
          closeCarrier();
          setSelectedCarrier(null);
        }}
        carrier={selectedCarrier}
        onSuccess={handleModalSuccess}
      />

      <ConfirmModal
        show={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setCarrierToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Kargo Firmasını Sil"
        message={`${carrierToDelete?.name} kargo firmasını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`}
        confirmText="Sil"
        cancelText="İptal"
        variant="danger"
        loading={actionLoading}
      />
    </div>
  );
}

export default CarrierManagement; 